import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Pin, PinOff, X } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// Hover is an enhancement. Click locks a panel; keyboard and touch never rely
// on hover. Radix handles collision placement, outside clicks and focus return.
export default function NavigationGroup({ item, mobile, collapsed, active, open, locked, onOpen,
  onClose, onLock, onEnter, onLeave, renderLeaf, groupBadge, testId }) {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const returnFocusRef = useRef(false);
  useEffect(() => { if (open && locked) returnFocusRef.current = true; }, [open, locked]);
  const buttonRef = useRef(null);
  const panelId = `nav-panel-${item.nameKey.replaceAll('.', '-')}`;
  const trigger = (
    <button ref={buttonRef} data-testid={testId} type="button" aria-expanded={open} aria-controls={open ? panelId : undefined}
      aria-label={collapsed ? [t(item.nameKey), groupBadge?.visible ? groupBadge.texto : ''].filter(Boolean).join(' · ') : undefined}
      onMouseEnter={() => !mobile && onEnter(item.nameKey)}
      onMouseLeave={() => !mobile && onLeave()}
      onClick={event => {
        event.preventDefault();
        if (open && (mobile || locked)) onClose();
        else onOpen(item.nameKey, true);
      }}
      onKeyDown={event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          onOpen(item.nameKey, true);
          if (open) panelRef.current?.querySelector('a')?.focus();
        }
      }}
      className={cn('w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors focus-visible:ring-2 focus-visible:ring-ring',
        (active || open) ? 'bg-sidebar-accent text-foreground font-medium' : 'text-sidebar-foreground hover:bg-sidebar-accent',
        collapsed && 'justify-center')}
    >
      <span className="relative shrink-0"><item.icon className={cn('h-5 w-5', (active || open) ? 'text-primary' : 'opacity-75')} />
        {collapsed && groupBadge?.visible && <span role="img" aria-label={groupBadge.texto} data-testid={`badge-group-${item.nameKey}`}
          className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] inline-flex items-center justify-center">{groupBadge.contenido}</span>}
      </span>
      {!collapsed && <><span className="flex-1 text-left">{t(item.nameKey)}</span>
        {groupBadge?.visible && <span role="img" aria-label={groupBadge.texto} data-testid={`badge-group-${item.nameKey}`} className="min-w-[18px] px-1 rounded-full bg-primary/15 text-primary text-xs">{groupBadge.contenido}</span>}
        {mobile ? <ChevronDown className={cn('h-4 w-4 opacity-60 transition-transform', open && 'rotate-180')} />
          : <ChevronRight className="h-4 w-4 opacity-50" />}</>}
    </button>
  );
  if (mobile) return <div>{trigger}{open && <div id={panelId} className="ml-3 mt-1 mb-2 border-l border-sidebar-border pl-2 space-y-1">{item.subItems.map(sub => renderLeaf(sub, true))}</div>}</div>;
  return <Popover open={open} onOpenChange={next => { if (!next) onClose(); }}>
    <PopoverTrigger asChild>{trigger}</PopoverTrigger>
    <PopoverContent ref={panelRef} id={panelId} side="right" align="start" sideOffset={10}
      aria-label={t(item.nameKey)}
      className="z-popover w-80 p-3 rounded-2xl bg-popover shadow-elev-3 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto"
      onMouseEnter={() => onEnter(null)} onMouseLeave={onLeave}
      onOpenAutoFocus={event => {
        event.preventDefault();
        if (locked) panelRef.current?.querySelector('a')?.focus();
      }}
      onCloseAutoFocus={event => { if (!returnFocusRef.current) event.preventDefault(); returnFocusRef.current = false; }}
    >
      {/* Transparent bridge covers the gap without blocking other area buttons. */}
      <div aria-hidden="true" className="absolute -left-3 top-0 bottom-0 w-3" onMouseEnter={() => onEnter(null)} />
      <div className="flex items-center justify-between gap-3 px-2 pb-3">
        <div><div className="text-base font-semibold">{t(item.nameKey)}</div>
          <div className="text-xs text-muted-foreground">{t(locked ? 'navigation.panel_pinned' : 'navigation.explore')}</div></div>
        <div className="flex gap-1">
          <button type="button" aria-pressed={locked} aria-label={t(locked ? 'navigation.unpin_panel' : 'navigation.pin_panel')}
            onClick={onLock} className="p-2 rounded-lg hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring">
            {locked ? <PinOff className="h-4 w-4 text-primary" /> : <Pin className="h-4 w-4" />}
          </button>
          <button type="button" aria-label={t('navigation.close_panel')} onClick={() => { onClose(); buttonRef.current?.focus(); }}
            className="p-2 rounded-lg hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="space-y-1">{item.subItems.map(sub => renderLeaf(sub, true))}</div>
      <div className="mt-3 border-t border-border pt-3 px-2 flex justify-between gap-3 text-xs text-muted-foreground">
        <span>{t(locked ? 'navigation.pinned_hint' : 'navigation.hover_hint')}</span><span className="whitespace-nowrap">{t('navigation.close_hint')}</span>
      </div>
    </PopoverContent>
  </Popover>;
}

